import type { AccountView, MailMessageView } from "@kwp/contracts";
import { useMutation } from "@tanstack/react-query";
import { useId, useRef, useState, type FormEvent, type Ref } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { Button, ErrorText } from "../../ui";
import { Drawer } from "../../ui/overlay";
import { useToast } from "../../ui/toast";
import { newRequestId } from "../tasks/use-task-actions";
import {
  applyFormat,
  formatRecipient,
  parseRecipients,
  plainText,
  replyRecipients,
  replySubject,
  textToHtml,
  validateDraft,
  type ComposeMode,
  type DraftErrors,
  type DraftFields,
  type Format,
} from "./compose-model";
import { useSendPresence } from "./presence";

/**
 * New mail, reply and forward (architecture §1, §9). Replies and forwards go out from the account
 * that received the message; new mail starts from the workspace's default From. Sending is
 * `mail.send` / `mail.forward` with a fresh request id (and, once available, the presence proof);
 * a send that needs approval says so with a link to Approvals.
 */
export type ComposeRequest = { mode: "new" } | { mode: "reply"; message: MailMessageView; accountAddress: string } | { mode: "forward"; message: MailMessageView };

function initialFields(req: ComposeRequest): DraftFields {
  if (req.mode === "reply") {
    return {
      to: replyRecipients(req.message, req.accountAddress).map(formatRecipient).join(", "),
      cc: "",
      bcc: "",
      subject: replySubject(req.message.subject),
      body: "",
    };
  }
  return { to: "", cc: "", bcc: "", subject: req.mode === "forward" ? req.message.subject : "", body: "" };
}

const TITLES: Record<ComposeMode, string> = { new: "New message", reply: "Reply", forward: "Forward" };

export function Composer({
  request,
  accounts,
  defaultFrom,
  onClose,
}: {
  request: ComposeRequest;
  accounts: readonly AccountView[];
  /** New mail's From: the workspace default, else an account of the workspace. */
  defaultFrom: string | null;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const toast = useToast();
  const id = useId();
  const [fields, setFields] = useState<DraftFields>(() => initialFields(request));
  const [from, setFrom] = useState<string>(() => (request.mode === "new" ? (defaultFrom === null ? "" : defaultFrom) : request.message.accountId));
  const [errors, setErrors] = useState<DraftErrors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);
  const body = useRef<HTMLTextAreaElement>(null);
  const confirmPresence = useSendPresence();
  const send = useMutation(trpc.mail.send.mutationOptions());
  const forward = useMutation(trpc.mail.forward.mutationOptions());
  const pending = send.isPending || forward.isPending;
  const fromAccount = accounts.find((a) => a.id === from);

  const set = (field: keyof DraftFields) => (value: string) => setFields((f) => ({ ...f, [field]: value }));

  function format(kind: Format) {
    const el = body.current;
    if (el === null) return;
    const next = applyFormat(fields.body, el.selectionStart, el.selectionEnd, kind);
    set("body")(next.value);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(next.start, next.end);
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const found = validateDraft(request.mode, fields);
    if (from === "" && found.to === undefined) found.to = "Connect a mail account first.";
    setErrors(found);
    setFailure(null);
    if (Object.keys(found).length > 0) return;
    try {
      if ((await confirmPresence()) === "cancelled") {
        setFailure("Not sent: confirm with your passkey to send.");
        return;
      }
      const requestId = newRequestId();
      const to = parseRecipients(fields.to).recipients;
      const outcome =
        request.mode === "forward"
          ? await forward.mutateAsync({ requestId, input: { messageId: request.message.id, to, note: fields.body } })
          : await send.mutateAsync({
              requestId,
              input: {
                accountId: from,
                to,
                cc: parseRecipients(fields.cc).recipients,
                bcc: parseRecipients(fields.bcc).recipients,
                subject: fields.subject,
                text: plainText(fields.body),
                html: textToHtml(fields.body),
                inReplyTo: request.mode === "reply" ? request.message.id : null,
              },
            });
      if (outcome.status === "awaiting_approval") {
        toast({ message: "Sending needs your approval.", action: { label: "Review in Approvals", href: "/approvals" } });
      } else {
        toast({ message: fromAccount === undefined ? "Sent." : `Sent from ${fromAccount.address}.` });
      }
      onClose();
    } catch (err) {
      setFailure(errorMessage(err));
    }
  }

  return (
    <Drawer title={TITLES[request.mode]} onClose={onClose} initialFocus={firstField}>
      <form aria-label={TITLES[request.mode]} onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-from`} className="text-sm font-medium text-text">
            From
          </label>
          <select
            id={`${id}-from`}
            value={from}
            disabled={request.mode !== "new"}
            onChange={(e) => setFrom(e.target.value)}
            className="min-h-9 rounded-md border border-border bg-surface px-2 text-sm text-text disabled:opacity-70"
          >
            {from === "" ? <option value="">No mail account</option> : null}
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.address}
              </option>
            ))}
          </select>
        </div>
        <Field id={`${id}-to`} label="To" value={fields.to} error={errors.to} onChange={set("to")} inputRef={firstField} />
        {request.mode === "forward" ? null : (
          <>
            <Field id={`${id}-cc`} label="Cc" value={fields.cc} error={errors.cc} onChange={set("cc")} />
            <Field id={`${id}-bcc`} label="Bcc" value={fields.bcc} error={errors.bcc} onChange={set("bcc")} />
            <Field id={`${id}-subject`} label="Subject" value={fields.subject} error={errors.subject} onChange={set("subject")} />
          </>
        )}
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <label htmlFor={`${id}-body`} className="text-sm font-medium text-text">
              {request.mode === "forward" ? "Note" : "Message"}
            </label>
            <div role="toolbar" aria-label="Formatting" className="flex gap-1">
              <Button variant="ghost" className="min-w-9 px-2 font-bold" onClick={() => format("bold")} aria-label="Bold">
                B
              </Button>
              <Button variant="ghost" className="min-w-9 px-2 italic" onClick={() => format("italic")} aria-label="Italic">
                I
              </Button>
              <Button variant="ghost" className="min-w-9 px-2" onClick={() => format("list")} aria-label="Bulleted list">
                •
              </Button>
            </div>
          </div>
          <textarea
            id={`${id}-body`}
            ref={body}
            rows={10}
            value={fields.body}
            aria-invalid={errors.body === undefined ? undefined : true}
            aria-describedby={`${id}-body-hint`}
            onChange={(e) => set("body")(e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
          />
          <p id={`${id}-body-hint`} className="text-xs text-muted">
            **bold**, _italic_ and lines starting “- ” become formatting.
            {request.mode === "forward" ? " The original message is attached below your note." : ""}
          </p>
          {errors.body === undefined ? null : <ErrorText>{errors.body}</ErrorText>}
        </div>
        {failure === null ? null : <ErrorText>{failure}</ErrorText>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={pending}>
            {pending ? "Sending…" : "Send"}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

function Field({
  id,
  label,
  value,
  error,
  onChange,
  inputRef,
}: {
  id: string;
  label: string;
  value: string;
  error: string | undefined;
  onChange: (value: string) => void;
  inputRef?: Ref<HTMLInputElement>;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-text">
        {label}
      </label>
      <input
        id={id}
        ref={inputRef}
        value={value}
        aria-invalid={error === undefined ? undefined : true}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-9 rounded-md border border-border bg-surface px-3 text-sm text-text"
      />
      {error === undefined ? null : <ErrorText>{error}</ErrorText>}
    </div>
  );
}
